import { computed, reactive, ref } from 'vue';
import { Attribute, BadgeTheme, PageInformation, Ticket, TicketStatus } from '../../../types';
import { useCoreSetup } from './core';
import { useI18n } from 'vue-i18n';
import { useToast } from './toast';
import { useTicketStore } from '../stores/ticketStore';
import { fileToBase64 } from '../../../utils/fileUtils';

export const NAME = 'ticket';
export const PAGE_SIZE_LOCAL_STORAGE_KEY = 'ticket-page_size';
export const DEFAULT_PAGE_SIZE = 15;

export function getStoredPageSize(): number {
    const stored = localStorage.getItem(PAGE_SIZE_LOCAL_STORAGE_KEY);
    if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return DEFAULT_PAGE_SIZE;
}

const ATTRIBUTES: Attribute[] = [
    {
        name: 'number',
        isSortable: false,
        isSearchable: false,
        position: 1,
    },
    {
        name: 'subject',
        isSortable: false,
        isSearchable: true,
        position: 2,
    },
    {
        name: 'created_at',
        isSortable: true,
        isSearchable: false,
        position: 3,
    },
    {
        name: 'updated_at',
        isSortable: true,
        isSearchable: false,
        position: 4,
    },
    {
        name: 'status',
        isSortable: false,
        isSearchable: false,
        position: 5,
    },
];

const search = ref('');
const sorting = ref('updated_at:desc');
const filters = ref({ status: TicketStatus.Open });
const formFields = reactive({
    topic: '',
    subject: '',
    description: '',
    attachment: null as File | null,
});

const resetFormFields = () => {
    formFields.topic = '';
    formFields.subject = '';
    formFields.description = '';
    formFields.attachment = null;
};

const isTicketDrawerOpen = ref(false);
const openTicketDrawer = () => {
    isTicketDrawerOpen.value = true;
};

const closeTicketDrawer = () => {
    isTicketDrawerOpen.value = false;
};

const ticketSubject = ref('');
const setTicketSubject = (subject: string) => {
    ticketSubject.value = subject;
};

const openTicketDrawerWithSubject = (subject: string) => {
    setTicketSubject(subject);
    openTicketDrawer();
};

const filesToAttachments = async (
    files: File[]
): Promise<Array<{ name: string; content_base64: string; content_type: string }>> => {
    return Promise.all(
        files.map(async (file) => ({
            name: file.name,
            content_base64: await fileToBase64(file),
            content_type: file.type,
        }))
    );
};

export function useTicketSetup() {
    const { buildAttributes, diffInWords } = useCoreSetup();
    const ticketStore = useTicketStore();

    const { addErrorToast } = useToast();
    const { t } = useI18n();

    const attributes = computed(() => buildAttributes(ATTRIBUTES, NAME));

    const pageUpdate = async ({ page, pageSize }: PageInformation, sourceType) => {
        try {
            const targetPageSize = pageSize ?? ticketStore.fetchPaginatedTicketsMeta?.per_page;

            if (pageSize !== undefined) {
                localStorage.setItem(PAGE_SIZE_LOCAL_STORAGE_KEY, String(pageSize));
            }

            await ticketStore.fetchPaginatedTickets(
                sourceType,
                page,
                targetPageSize,
                [sorting.value],
                filters.value,
                search.value
            );
        } catch {
            addErrorToast(t('globalMessages.serverError.message'));
        }
    };

    function decorateStatusTheme(ticket: Ticket) {
        switch (ticket.status) {
            case TicketStatus.Open:
                return BadgeTheme.Danger;
            case TicketStatus.Pending:
                return BadgeTheme.Primary;
            case TicketStatus.Solved:
                return BadgeTheme.Success;
            case TicketStatus.Closed:
                return BadgeTheme.Secondary;
            default:
                return BadgeTheme.Secondary;
        }
    }

    function decorateStatusText(ticket: Ticket) {
        return t(`resources.ticket.enums.status.${ticket.status}`);
    }

    function decorateStatusHint(ticket: Ticket) {
        return t(`resources.ticket.enums.statusHint.${ticket.status}`);
    }

    function decorateCreatedAt(ticket: Ticket) {
        return diffInWords(ticket.created_at);
    }

    function decorateUpdatedAt(ticket: Ticket) {
        return diffInWords(ticket.updated_at);
    }

    function toTicketById(id: number) {
        return { name: 'TicketPage', params: { id } };
    }

    return {
        attributes,
        search,
        sorting,
        filters,
        isTicketDrawerOpen,
        ticketSubject,
        openTicketDrawer,
        closeTicketDrawer,
        openTicketDrawerWithSubject,
        pageUpdate,
        decorateStatusTheme,
        decorateStatusText,
        decorateStatusHint,
        decorateUpdatedAt,
        decorateCreatedAt,
        toTicketById,
        filesToAttachments,
    };
}

export function useTicket() {
    return {
        sorting,
        formFields,
        resetFormFields,
        filesToAttachments,
        isTicketDrawerOpen,
        ticketSubject,
        openTicketDrawer,
        closeTicketDrawer,
        openTicketDrawerWithSubject,
    };
}
